import * as XLSX from 'xlsx';
import { getStatusConfig, formatOrderDate, getCustomerName, getPaymentLabel, normalizeSalesChannel } from './orderUtils';
import { esCorreoSintetico } from './correoCliente';
import { decodeHtml } from './decodeHtml';

// ── Helpers compartidos para exportaciones de Resumen ─────────────────────────

const _CASH_RE     = /(efectivo|cash|contra\s*entrega|\bcod\b)/i;
const _TRANSFER_RE = /(transfer|transferencia|banco|bancolombia)/i;

function _normalizePayment(order) {
  const p = getPaymentLabel(order);
  if (p.mixed) return 'Pago mixto';
  const l = String(p.label || '');
  if (_CASH_RE.test(l))     return 'Efectivo';
  if (_TRANSFER_RE.test(l)) return 'Transferencia';
  return l || 'No registrado';
}

function _getVarLabel(item) {
  return (item.meta_data || [])
    .filter(m => m.display_key && !m.key?.startsWith('_') && m.display_value)
    .map(m => m.display_value)
    .join(' / ') || '';
}

// El título del producto para una celda: el nombre del PADRE, no el de una
// variación. Woo manda `name` YA concatenado como «Padre - Opción1, Opción2», y
// el nombre limpio existe SÓLO en `parent_name`, que el backend deja pasar en la
// lista blanca de `mapOrder` (`server.js:827`). No se puede derivar partiendo por
// « - »: las opciones mismas lo contienen («PB&J - warm berry brown»).
//
// El `|| item.name` NO es defensivo: es una RAMA DE PRODUCCIÓN. Está medido que
// las líneas SIN variación llegan con `parent_name` vacío — 10/1136 en la ventana
// 2026-06-01→08-12, y 0 de esas 10 traía parent_name (`scripts/censo-titulo-resumen.js`
// §3). Sin el `||`, esas líneas quedarían con el título EN BLANCO.
//
// Sirve para todos los exports vivos, que se alimentan de `mapOrder` (`/api/orders`)
// y traen `parent_name` con ese contrato. (Los exports de `/api/export/orders`, que
// leían `item.variation` del endpoint, se BORRARON en C4 junto con la ruta.)
function _tituloProducto(item) {
  return decodeHtml(item.parent_name || item.name || '') || '';
}

// El rótulo de variación ya legible, para escribir en una celda.
//
// La decodificación va ACÁ, al escribir, y NUNCA dentro de `_getVarLabel`:
// `exportResumenByProduct` usa la salida CRUDA de `_getVarLabel` como parte de su
// clave de agrupación cuando la línea no trae `variation_id`. Decodificar aguas
// arriba movería esa clave —dos etiquetas codificadas distintas pueden decodificar
// al mismo texto y fundirían dos grupos que hoy son dos—, y este cambio es
// presentacional: no toca qué se agrupa con qué.
function _varLabelVisible(item) {
  return decodeHtml(_getVarLabel(item)) || '';
}

function _buildHeader(storeName, title, filterInfo) {
  const now = new Date().toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return [
    [storeName ? `${storeName} — ${title}` : title],
    [`${filterInfo} | Generado: ${now}`],
  ];
}

function _resumenFilename(prefix, periodLabel) {
  const safe = String(periodLabel || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()
    .slice(0, 40);
  return `${prefix}${safe ? `-${safe}` : ''}.xlsx`;
}

function _boldRow(ws, rowIdx, colCount) {
  for (let c = 0; c < colCount; c++) {
    const ref = XLSX.utils.encode_cell({ r: rowIdx, c });
    if (ws[ref]) ws[ref].s = { font: { bold: true } };
  }
}

// ── Exportación "Por pedido": una fila por línea de producto por pedido ────────

export function exportResumenByOrder(orders, {
  storeName = '',
  periodLabel = '',
  canalLabel = 'Todos los canales',
  beautyIds = null,
  beautyLabel = '', // name del negocio no-base (columna Categoría) — nada hardcodeado acá
  productFilter = 'all',
} = {}) {
  const columns = [
    'Fecha', 'N° Pedido', 'Cliente', 'Producto', 'Categoría', 'Variación',
    'Cantidad vendida', 'Precio unitario', 'Total facturado', 'Método de pago', 'Canal de venta',
  ];

  const dataRows = [];
  let totalQty = 0;
  let totalAmount = 0;

  for (const order of orders) {
    const fecha    = formatOrderDate(order.date_created);
    const numPed   = `#${order.number}`;
    const cliente  = getCustomerName(order);
    const pago     = _normalizePayment(order);
    const canal    = normalizeSalesChannel(order).label || 'Otro';

    for (const item of order.line_items || []) {
      const pid = Number(item.product_id) || 0;
      if (productFilter === 'beauty' && beautyIds && !beautyIds.has(pid)) continue;

      const qty       = Number(item.quantity) || 0;
      const total     = parseFloat(item.total) || 0;
      const unitPrice = qty > 0 ? parseFloat((total / qty).toFixed(2)) : (parseFloat(item.price) || 0);
      const categoria = beautyIds?.has(pid) ? beautyLabel : '';

      totalQty    += qty;
      totalAmount += total;

      dataRows.push([
        fecha, numPed, cliente,
        _tituloProducto(item), categoria, _varLabelVisible(item),
        qty, unitPrice, parseFloat(total.toFixed(2)),
        pago, canal,
      ]);
    }
  }

  if (dataRows.length === 0) return;

  const filterInfo = `Período: ${periodLabel} | Canal: ${canalLabel}${productFilter === 'beauty' ? ` | ${beautyLabel}` : ''}`;
  const headerLines = _buildHeader(storeName, 'Reporte de ventas por pedido', filterInfo);

  const totalsRow = ['TOTAL', '', '', '', '', '', totalQty, '', parseFloat(totalAmount.toFixed(2)), '', ''];

  const aoa = [...headerLines, [], columns, ...dataRows, totalsRow];
  const ws  = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = [
    { wch: 14 }, { wch: 12 }, { wch: 26 }, { wch: 40 },
    { wch: 16 }, { wch: 26 }, { wch: 18 }, { wch: 16 },
    { wch: 18 }, { wch: 18 }, { wch: 18 },
  ];

  // Totals row index: 2 header rows + 1 blank + 1 columns row + N data rows
  _boldRow(ws, headerLines.length + 1 + 1 + dataRows.length, columns.length);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Por pedido');
  XLSX.writeFile(wb, _resumenFilename('resumen-por-pedido', periodLabel));
}

// ── Exportación "Por producto": agrupado por producto/variación + método + canal

export function exportResumenByProduct(orders, {
  storeName = '',
  periodLabel = '',
  canalLabel = 'Todos los canales',
  beautyIds = null,
  beautyLabel = '', // name del negocio no-base (columna Categoría) — nada hardcodeado acá
  productFilter = 'all',
} = {}) {
  const columns = [
    'Producto', 'Categoría', 'Variación',
    'Cantidad total vendida', 'Precio unitario', 'Total facturado',
    'Método de pago', 'Canal de venta',
  ];

  // Group by product + variation + payment method + canal
  const map = new Map();

  for (const order of orders) {
    const pago  = _normalizePayment(order);
    const canal = normalizeSalesChannel(order).label || 'Otro';

    for (const item of order.line_items || []) {
      const pid = Number(item.product_id) || 0;
      if (productFilter === 'beauty' && beautyIds && !beautyIds.has(pid)) continue;

      // `varLabel` queda CRUDO a propósito: entra en `varKey`, o sea en la clave de
      // agrupación. Lo que se decodifica es la celda, no la clave (ver `_varLabelVisible`).
      const varLabel = _getVarLabel(item);
      const varKey   = item.variation_id ? String(item.variation_id) : (varLabel || '__simple__');
      const mapKey   = `${pid}|${varKey}|${pago}|${canal}`;

      if (!map.has(mapKey)) {
        map.set(mapKey, {
          Producto:  _tituloProducto(item),
          Categoría: beautyIds?.has(pid) ? beautyLabel : '',
          Variación: decodeHtml(varLabel) || '',
          qty:       0,
          total:     0,
          pago,
          canal,
        });
      }
      const entry = map.get(mapKey);
      entry.qty   += Number(item.quantity) || 0;
      entry.total += parseFloat(item.total) || 0;
    }
  }

  if (map.size === 0) return;

  const entries = [...map.values()].sort(
    (a, b) => b.qty - a.qty || a.Producto.localeCompare(b.Producto)
  );

  let totalQty = 0;
  let totalAmount = 0;

  const dataRows = entries.map(e => {
    const qty       = e.qty;
    const total     = parseFloat(e.total.toFixed(2));
    const unitPrice = qty > 0 ? parseFloat((total / qty).toFixed(2)) : 0;
    totalQty    += qty;
    totalAmount += total;
    return [e.Producto, e.Categoría, e.Variación, qty, unitPrice, total, e.pago, e.canal];
  });

  const filterInfo = `Período: ${periodLabel} | Canal: ${canalLabel}${productFilter === 'beauty' ? ` | ${beautyLabel}` : ''}`;
  const headerLines = _buildHeader(storeName, 'Reporte de ventas por producto', filterInfo);

  const totalsRow = ['TOTAL', '', '', totalQty, '', parseFloat(totalAmount.toFixed(2)), '', ''];

  const aoa = [...headerLines, [], columns, ...dataRows, totalsRow];
  const ws  = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = [
    { wch: 40 }, { wch: 16 }, { wch: 26 },
    { wch: 22 }, { wch: 16 }, { wch: 18 },
    { wch: 18 }, { wch: 18 },
  ];

  _boldRow(ws, headerLines.length + 1 + 1 + dataRows.length, columns.length);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Por producto');
  XLSX.writeFile(wb, _resumenFilename('resumen-por-producto', periodLabel));
}

// ── Helpers de nombre de archivo ──────────────────────────────────────────────

function _filenameDate() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}${mm}${d.getFullYear()}`;
}

function _safeLabel(s) {
  return String(s || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/gi, '').slice(0, 30);
}

// ── Todos los pedidos — una fila por línea de producto ─────────────────────────

export function exportAllOrdersTab(orders, { storeName = '', filterLabel = 'Todos' } = {}) {
  const columns = [
    'Fecha', 'N° Pedido', 'Cliente', 'Email',
    'Producto', 'Variación',
    'Cantidad', 'Precio unitario', 'Total línea',
    'Subtotal pedido', 'Descuento', 'Envío', 'Total pedido',
    'Método de pago', 'Canal de venta', 'Estado', 'Notas',
  ];

  const dataRows = [];
  let totalQty = 0;
  let totalAmount = 0;

  for (const order of orders) {
    const fecha    = formatOrderDate(order.date_created);
    const numPed   = `#${order.number}`;
    const cliente  = getCustomerName(order);
    const email    = esCorreoSintetico(order.billing?.email) ? '' : (order.billing?.email || '');
    const pago     = getPaymentLabel(order).label || '';
    const canal    = normalizeSalesChannel(order).label || 'Desconocido';
    const estado   = getStatusConfig(order.status).label;
    const notas    = order.customer_note || '';
    const subtotal  = parseFloat(order.subtotal       || 0);
    const descuento = parseFloat(order.discount_total || 0);
    const envio     = parseFloat(order.shipping_total || 0);
    const total     = parseFloat(order.total          || 0);

    const items = order.line_items || [];
    if (items.length === 0) {
      dataRows.push([fecha, numPed, cliente, email, '', '', 0, 0, 0, subtotal, descuento, envio, total, pago, canal, estado, notas]);
    } else {
      for (let idx = 0; idx < items.length; idx++) {
        const item      = items[idx];
        const qty       = Number(item.quantity) || 0;
        const lineTotal = parseFloat(item.total || 0);
        const unitPrice = qty > 0 ? parseFloat((lineTotal / qty).toFixed(2)) : 0;
        totalQty    += qty;
        totalAmount += lineTotal;
        dataRows.push([
          fecha, numPed, cliente, email,
          _tituloProducto(item), _varLabelVisible(item),
          qty, unitPrice, parseFloat(lineTotal.toFixed(2)),
          idx === 0 ? subtotal  : '',
          idx === 0 ? descuento : '',
          idx === 0 ? envio     : '',
          idx === 0 ? total     : '',
          idx === 0 ? pago      : '',
          idx === 0 ? canal     : '',
          idx === 0 ? estado    : '',
          idx === 0 ? notas     : '',
        ]);
      }
    }
  }

  const headerLines = _buildHeader(storeName, 'Módulo de Pedidos', `Filtro: ${filterLabel}`);
  const totalsRow   = Array(columns.length).fill('');
  totalsRow[0] = 'TOTALES';
  totalsRow[6] = totalQty;
  totalsRow[8] = parseFloat(totalAmount.toFixed(2));

  const aoa = [...headerLines, [], columns, ...dataRows, totalsRow];
  const ws  = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [
    { wch: 16 }, { wch: 12 }, { wch: 26 }, { wch: 30 },
    { wch: 36 }, { wch: 24 },
    { wch: 10 }, { wch: 16 }, { wch: 14 },
    { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 14 },
    { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 30 },
  ];
  _boldRow(ws, headerLines.length + 1 + 1 + dataRows.length, columns.length);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pedidos');
  XLSX.writeFile(wb, `Pedidos_${_safeLabel(filterLabel)}_${_filenameDate()}.xlsx`);
}

// ── Envíos pendientes ─────────────────────────────────────────────────────────

export function exportShippingPending(orders, { storeName = '' } = {}) {
  const columns = [
    'Fecha pedido', 'N° Pedido', 'Cliente', 'Teléfono',
    'Dirección de envío', 'Productos',
    'Total pedido', 'Método de pago', 'Canal', 'Estado envío',
  ];

  const dataRows = orders.map((order) => {
    const shipping = order.shipping || {};
    const billing  = order.billing  || {};
    const addr = [
      shipping.address_1 || billing.address_1,
      shipping.city      || billing.city,
      shipping.state     || billing.state,
    ].filter(Boolean).join(', ');
    const productos = (order.line_items || [])
      // Antes escribía «Padre - Rojo, M [Rojo / M]»: la variación, dos veces.
      // Con el título limpio queda «Padre [Rojo / M]», que no pierde nada.
      .map((i) => { const v = _varLabelVisible(i); return `${_tituloProducto(i)}${v ? ` [${v}]` : ''} x${i.quantity}`; })
      .join(' / ');
    return [
      formatOrderDate(order.date_created),
      `#${order.number}`,
      getCustomerName(order),
      billing.phone || '',
      addr,
      productos,
      parseFloat(order.total || 0),
      getPaymentLabel(order).label || '',
      normalizeSalesChannel(order).label || 'Desconocido',
      order.estado_envio || 'Pendiente',
    ];
  });

  const headerLines = _buildHeader(storeName, 'Envíos pendientes', `Generado: ${new Date().toLocaleDateString('es-CO')}`);
  const aoa = [...headerLines, [], columns, ...dataRows];
  const ws  = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [
    { wch: 16 }, { wch: 12 }, { wch: 24 }, { wch: 14 },
    { wch: 40 }, { wch: 50 },
    { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 14 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pendientes');
  XLSX.writeFile(wb, `EnviosPendientes_${_filenameDate()}.xlsx`);
}

// ── Ya enviados ───────────────────────────────────────────────────────────────

export function exportShippingShipped(orders, { storeName = '', filterLabel = 'Todos' } = {}) {
  const columns = [
    'Fecha pedido', 'N° Pedido', 'Cliente',
    'Fecha de envío', 'Tipo transporte', 'Transportista',
    'Cobrado al cliente', 'Costo real', 'Diferencia',
    'Forma de pago transporte', 'Canal', 'Estado',
  ];

  let totalCobrado = 0;
  let totalCosto   = 0;

  const dataRows = orders.map((order) => {
    const cost    = order.costo_envio_real;
    const charged = order.envio_cobrado_cliente;
    const diff    = (typeof charged === 'number' && typeof cost === 'number')
      ? parseFloat((charged - cost).toFixed(2)) : '';
    totalCobrado += typeof charged === 'number' ? charged : 0;
    totalCosto   += typeof cost    === 'number' ? cost    : 0;
    return [
      formatOrderDate(order.date_created),
      `#${order.number}`,
      getCustomerName(order),
      order.fecha_envio || '',
      order.tipo_transporte === 'empresa' ? 'Empresa' : order.tipo_transporte === 'mensajero' ? 'Mensajero' : '',
      order.nombre_transportista || '',
      charged != null ? charged : '',
      cost    != null ? cost    : '',
      diff,
      order.pago_envio_metodo || '',
      normalizeSalesChannel(order).label || 'Desconocido',
      'Enviado',
    ];
  });

  const totalsRow = Array(columns.length).fill('');
  totalsRow[0] = 'TOTALES';
  totalsRow[6] = parseFloat(totalCobrado.toFixed(2));
  totalsRow[7] = parseFloat(totalCosto.toFixed(2));
  totalsRow[8] = parseFloat((totalCobrado - totalCosto).toFixed(2));

  const headerLines = _buildHeader(storeName, 'Envíos despachados', `Filtro: ${filterLabel}`);
  const aoa = [...headerLines, [], columns, ...dataRows, totalsRow];
  const ws  = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [
    { wch: 16 }, { wch: 12 }, { wch: 24 },
    { wch: 16 }, { wch: 14 }, { wch: 24 },
    { wch: 18 }, { wch: 14 }, { wch: 14 },
    { wch: 20 }, { wch: 16 }, { wch: 12 },
  ];
  _boldRow(ws, headerLines.length + 1 + 1 + dataRows.length, columns.length);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Enviados');
  XLSX.writeFile(wb, `EnviosEnviados_${_safeLabel(filterLabel)}_${_filenameDate()}.xlsx`);
}

export function exportBeautyBreakdown(breakdown, { storeName = '', periodLabel = '', beautyLabel = '' } = {}) {
  const { rows, totalUnits, grandTotal } = breakdown;
  const allOrders = rows.reduce((s, r) => s + r.orderCount, 0);

  const headerLines = _buildHeader(storeName, `${beautyLabel} — Desglose por método de pago`, `Período: ${periodLabel}`);
  const columns = ['Método de pago', 'Total recaudado (COP)', 'Unidades vendidas', 'Pedidos'];
  const dataRows = rows.map((r) => [r.method, parseFloat(r.total.toFixed(2)), r.units, r.orderCount]);
  const totalsRow = ['TOTAL', parseFloat(grandTotal.toFixed(2)), totalUnits, allOrders];

  const aoa = [...headerLines, [], columns, ...dataRows, totalsRow];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 34 }, { wch: 22 }, { wch: 20 }, { wch: 12 }];
  _boldRow(ws, headerLines.length + 1 + 1 + dataRows.length, columns.length);

  const wb = XLSX.utils.book_new();
  // Nombre de hoja: Excel limita a 31 chars y prohíbe : \ / ? * [ ] — se sanea.
  const hoja = (beautyLabel || 'Negocio').replace(/[:\\/?*[\]]/g, ' ').slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, hoja);
  XLSX.writeFile(wb, _resumenFilename(_safeLabel(beautyLabel || 'negocio'), periodLabel));
}
